import React from "react";
import { connect } from "react-redux";
import {
  wordPlacedSuccess,
  wordPlacedFailure,
  allWordsPlaced,
  draggingWord,
  bucketHover
} from "../actions/wordActions";
import { isCorrectMatch } from "../utils/wordMatching";
import { BUCKET_HOVER_COLOR, CORRECT_DROP_COLOR, CORRECT_WORD_TEXT_COLOR, INCORRECT_DROP_COLOR }
  from "../utils/dragColors";

class DraggableSnap extends React.Component {
  constructor(props) {
    super(props);
    this.touch_move = this.touch_move.bind(this);
    this.touch_end = this.touch_end.bind(this);
    this.touch_cancel = this.touch_cancel.bind(this);
    this.hovered_bucket = null;
    // tracks whether the current touch gesture actually moved, so a plain
    // tap (touchstart+touchend, no movement) can be told apart from a
    // real drag and handled as a tap-to-select instead
    this.touch_moved = false;
    this.last_touch_end_time = 0;
  }
  set_dragging_word(event){
    this.props.dispatch(draggingWord(this.props.number))
  }
  handle_touch_start(event){
    this.touch_moved = false;
  }
  //select/deselect this word for tap-to-place, as an alternative to
  //dragging - clicking an already-selected word deselects it, clicking a
  //different one switches the selection
  toggle_selection(){
    if (this.props.dragged_word_number === this.props.number){
      this.props.dispatch(draggingWord(-1));
    }
    else{
      this.props.dispatch(draggingWord(this.props.number));
    }
  }
  handle_click(event){
    // mobile browsers can fire a synthetic click shortly after a touchend
    // with no movement; touch_end already handled the tap as a selection
    // toggle, so skip re-toggling it here
    if (Date.now() - this.last_touch_end_time < 500){
      return;
    }
    this.toggle_selection();
  }
  //Enter/Space activates this word the same way a click/tap does - plain
  //divs (needed for native HTML5 drag) get no keyboard activation for free
  handle_key_down(event){
    if (event.key === 'Enter' || event.key === ' '){
      event.preventDefault();
      this.toggle_selection();
    }
  }
  componentDidMount() {
    this.input.addEventListener('touchmove', this.touch_move, { passive: false });
    this.input.addEventListener('touchend', this.touch_end, { passive: false });
    this.input.addEventListener('touchcancel', this.touch_cancel, { passive: false });
  }
  componentWillUnmount() {
    this.input.removeEventListener('touchmove', this.touch_move);
    this.input.removeEventListener('touchend', this.touch_end);
    this.input.removeEventListener('touchcancel', this.touch_cancel);
  }
  //ensure this returns false when we don't match a draggable with the correct wordbucket
  //
  //bucket_number defaults to the redux-tracked last-hovered bucket (kept
  //live in sync with the actual pointer during a touch gesture, so it's
  //safe there) - native mouse drag passes its own freshly-computed value
  //instead, since dragover/bucketHover only fires while the cursor is over
  //a bucket, leaving redux state stale (pointing at whatever was hovered
  //earlier in the gesture) if the drop itself lands outside any bucket
  check_drag_match(draggable_element_number,event,bucket_number=this.props.bucket_number){
    // compares word *text*, not position - a repeated word (e.g. "to get
    // to the X") has two identical-looking tiles, either of which is
    // accepted for either matching (not-yet-filled) bucket
    if (isCorrectMatch(this.props.punchline_words, this.props.words_correct, draggable_element_number, bucket_number)){
        this.props.dispatch(wordPlacedSuccess(bucket_number))
        event.currentTarget.style.visibility = "hidden"
        let placed_word = document.getElementById("invisible_"+String(bucket_number))
        placed_word.style.color = CORRECT_WORD_TEXT_COLOR;
        placed_word.style.border = "";
        placed_word.style.backgroundColor = BUCKET_HOVER_COLOR;
        this.props.dispatch(allWordsPlaced())
        return true
      }
      // a bucket that's already correctly filled should be left alone -
      // only a genuine text mismatch should mark it incorrect. Without this,
      // dropping the "other" copy of a repeated word onto an already-solved
      // bucket would wrongly un-solve it.
      if (!Array.isArray(bucket_number) && this.props.words_correct[bucket_number]){
        return false
      }
      this.props.dispatch(wordPlacedFailure(bucket_number))
      if (!Array.isArray(bucket_number)){
        let missed_bucket = document.getElementById("invisible_"+String(bucket_number))
        if (missed_bucket){
          missed_bucket.style.backgroundColor = BUCKET_HOVER_COLOR;
        }
      }
      return false
  }

  async snap(event){
    // recompute the real drop target from where the pointer actually is,
    // rather than trusting redux's last-hovered bucket_number - that value
    // only updates while the cursor is over a bucket (onDragOver), so a
    // drop that lands outside every bucket (released over the header, a
    // margin, between tiles, etc.) would otherwise still match/hide the
    // word against whatever bucket was hovered earlier in the drag
    const drop_element = document.elementFromPoint(event.clientX, event.clientY);
    const bucket_el = drop_element ? drop_element.closest('.Hidden-Word') : null;
    if (bucket_el) {
      const draggable_element_number = Number(event.target.id.substring(event.target.id.indexOf("_")+1))
      const bucket_number = Number(bucket_el.id.slice(bucket_el.id.indexOf('_')+1))
      this.check_drag_match(draggable_element_number,event,bucket_number)
    }
    else{
      event.currentTarget.style.position = ""
      this.props.dispatch(wordPlacedFailure(this.props.bucket_number))
    }
    this.props.dispatch(draggingWord(-1))

  }
  touch_end(event){
    event.preventDefault()
    this.last_touch_end_time = Date.now();
    if (!this.touch_moved){
      // a plain tap, no drag - toggle selection instead of resolving a drop
      this.toggle_selection();
      return;
    }
    const bucket = this.hovered_bucket;
    if (bucket){
      bucket.style.backgroundColor = BUCKET_HOVER_COLOR;
    }
    this.hovered_bucket = null;
    // resolve the match (which hides the word on success) before
    // resetting position - otherwise the word briefly snaps back to its
    // palette position for a frame before disappearing.
    //
    // bucket_number is read directly off `bucket` (the DOM element
    // touch_move already found via elementFromPoint), not left to
    // check_drag_match's this.props.bucket_number default - that's
    // redux state, and dispatch(bucketHover(...)) in touch_move's own
    // last call updates the store immediately but this component's
    // props don't reflect it until React re-renders, which a touchend
    // firing right after that same touchmove (a fast flick, or - 100% of
    // the time - a synthetic drag with no real gap between events, like
    // tests_selenium.py's TOUCH_DRAG_JS) can beat entirely. `bucket`
    // itself has no such lag - it's the actual element just hovered.
    if (bucket){
      const bucket_number = Number(bucket.id.slice(bucket.id.indexOf('_')+1))
      this.check_drag_match(this.props.number, {currentTarget: event.target}, bucket_number)
    }
    else {
      this.props.dispatch(wordPlacedFailure(this.props.bucket_number))
    }
    event.target.style.position = ""
    event.target.style.left = ""
    event.target.style.top = ""
    this.props.dispatch(draggingWord(-1))
  }
  touch_cancel(event){
    if (this.hovered_bucket){
      this.hovered_bucket.style.backgroundColor = BUCKET_HOVER_COLOR;
    }
    this.hovered_bucket = null;
    event.target.style.position = ""
    event.target.style.left = ""
    event.target.style.top = ""
    if (this.touch_moved){
      this.props.dispatch(draggingWord(-1));
    }
  }
  touch_move(event){
    event.preventDefault()
    if (!this.touch_moved){
      this.touch_moved = true;
      this.props.dispatch(draggingWord(this.props.number));
    }
    let location = event.changedTouches[0]
    // fixed (not absolute) + viewport-relative clientX/Y, not page-relative
    // pageX/Y - #dragElements is itself position:relative, so absolute+pageX/Y
    // placed the word offset by #dragElements' own distance from the
    // document origin, well below the actual touch point
    event.target.style.position = "fixed";
    event.target.style.left = String(location.clientX)+"px";
    event.target.style.top = String(location.clientY)+"px";
    //hide the dragged word from hit-testing so elementFromPoint sees what's underneath it
    const previous_pointer_events = event.target.style.pointerEvents;
    event.target.style.pointerEvents = 'none';
    let touch_location_element = document.elementFromPoint(location.clientX, location.clientY);
    event.target.style.pointerEvents = previous_pointer_events;
    const bucket = touch_location_element ? touch_location_element.closest('.Hidden-Word') : null;
    if (bucket === this.hovered_bucket){
      return;
    }
    if (this.hovered_bucket){
      this.hovered_bucket.style.backgroundColor = BUCKET_HOVER_COLOR;
    }
    if (bucket){
      const bucket_number = Number(bucket.id.slice(bucket.id.indexOf('_')+1))
      const rect = bucket.getBoundingClientRect();
      this.props.dispatch(bucketHover([rect.x,rect.y], bucket_number))
      bucket.style.backgroundColor = isCorrectMatch(this.props.punchline_words, this.props.words_correct, this.props.number, bucket_number) ? CORRECT_DROP_COLOR : INCORRECT_DROP_COLOR;
    }
    else {
      this.props.dispatch(bucketHover([0,0], []))
    }
    this.hovered_bucket = bucket;
  }
  render() {
    const selected = this.props.dragged_word_number === this.props.number;
    return(
       <div className={'Draggable-Word' + (selected ? ' selected' : '')} draggable='true'  id={this.props.order}
        role="button" tabIndex={0} aria-pressed={selected}
        aria-label={'Word: ' + this.props.word + (selected ? ' (selected - choose a blank to place it)' : '')}
        onDragStart={(event) => this.set_dragging_word(event)}
        onDragEnd={(event) => this.snap(event)}
        onClick={(event) => this.handle_click(event)}
        onKeyDown={(event) => this.handle_key_down(event)}
        onTouchStart={(event) => this.handle_touch_start(event)}
        ref={ref => this.input = ref}>
        {this.props.word}

      </div>
    );
  }
}

const mapStateToProps = state => ({
  position: state.wordReducer.position,
  bucket_number: state.wordReducer.bucket_number,
  dragged_word_number: state.wordReducer.dragged_word_number,
  solved: state.jokeReducer.solved,
  words_correct:state.jokeReducer.words_correct,
  punchline_words: state.jokeReducer.punchline_words,
  mute: state.jokeReducer.mute
});

export default connect(mapStateToProps)(DraggableSnap);
